import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Trash2, Loader2, Save, CalendarClock, FileText, Zap } from 'lucide-react';

const REMINDER_PRESETS = [30, 14, 7, 3, 1];

const invoiceStatusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  draft: 'secondary',
  sent: 'info',
  paid: 'success',
  partial: 'warning',
  overdue: 'destructive',
  cancelled: 'secondary',
  void: 'secondary',
};

interface GeneratedInvoice {
  id: string;
  invoiceNumber: number;
  status: string;
  issueDate: string;
  dueDate: string;
  total: number;
  amountPaid: number;
}

/** Stored as a JSON array string like "[14,7,3,1]" */
function parseReminderDays(raw: string | null | undefined): number[] {
  if (!raw) return [];
  const matches = String(raw).match(/\d+/g);
  if (!matches) return [];
  return Array.from(new Set(matches.map(Number))).sort((a, b) => b - a);
}

function billingDayLabel(day: number): string {
  if (day >= 31) return 'Last day of month';
  const suffix = day % 10 === 1 && day !== 11 ? 'st' : day % 10 === 2 && day !== 12 ? 'nd' : day % 10 === 3 && day !== 13 ? 'rd' : 'th';
  return `${day}${suffix} of the month`;
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** Mirrors the server's next_billing_date calculation */
function firstBillingDate(startDate: string, billingDay: number): Date | null {
  if (!startDate) return null;
  const [y, m, d] = startDate.split('T')[0].split('-').map(Number);
  if (!y || !m || !d) return null;
  let year = y;
  let month = m - 1;
  if (d > billingDay) {
    month += 1;
    if (month > 11) {
      month = 0;
      year += 1;
    }
  }
  return new Date(Date.UTC(year, month, Math.min(billingDay, daysInMonth(year, month))));
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: number;
  sortOrder: number;
  itemType: string;
}

interface Account { id: string; name: string; }
interface BusinessProfile { id: string; name: string; }
interface Contract { id: string; title: string; accountId: string; }

interface ScheduleData {
  accountId: string;
  businessProfileId: string;
  contractId: string | null;
  applicationId: string | null;
  name: string;
  frequency: string;
  billingDayOfMonth: number;
  startDate: string;
  endDate: string | null;
  status: string;
  paymentTerms: string;
  taxRate: number;
  gracePeriodDays: number;
  sendInvoiceDaysBefore: number;
  enablePaymentReminders: boolean;
  reminderDaysBeforeDue: string;
  autoGenerate: boolean;
  nextBillingDate: string | null;
  lastGeneratedAt: string | null;
  generatedInvoiceCount: number;
  notes: string | null;
  items: LineItem[];
}

export function RecurringScheduleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;

  const [accountId, setAccountId] = useState('');
  const [businessProfileId, setBusinessProfileId] = useState('');
  const [contractId, setContractId] = useState('');
  const [name, setName] = useState('');
  const [frequency, setFrequency] = useState('monthly');
  const [billingDayOfMonth, setBillingDayOfMonth] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('active');
  const [paymentTerms, setPaymentTerms] = useState('Net 30');
  // Entered as a percentage (8.25) — converted to the stored decimal (0.0825) on submit
  const [taxRatePercent, setTaxRatePercent] = useState(0);
  const [gracePeriodDays, setGracePeriodDays] = useState(15);
  const [sendInvoiceDaysBefore, setSendInvoiceDaysBefore] = useState(0);
  const [enablePaymentReminders, setEnablePaymentReminders] = useState(true);
  const [reminderDays, setReminderDays] = useState<number[]>([14, 7, 3, 1]);
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [nextBillingDate, setNextBillingDate] = useState<string | null>(null);
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<LineItem[]>([
    { description: '', quantity: 1, unitPrice: 0, sortOrder: 0, itemType: 'service' },
  ]);

  // Fetch dropdown data
  const { data: accountsData } = useQuery({
    queryKey: ['invoiceAccounts'],
    queryFn: () => api.getInvoiceAccounts(),
  });

  const { data: profilesData } = useQuery({
    queryKey: ['businessProfiles'],
    queryFn: () => api.getBusinessProfiles(),
  });

  const { data: contractsData } = useQuery({
    queryKey: ['contracts', { accountId }],
    queryFn: () => api.getContracts({ accountId: accountId || undefined, limit: 100 }),
    enabled: !!accountId,
  });

  // Fetch existing schedule for edit
  const { data: scheduleData } = useQuery({
    queryKey: ['recurringSchedule', id],
    queryFn: () => api.getRecurringSchedule(id!),
    enabled: isEdit,
  });

  // Invoices this schedule has already produced
  const { data: generatedData } = useQuery({
    queryKey: ['scheduleInvoices', id],
    queryFn: () => api.getScheduleInvoices(id!),
    enabled: isEdit,
  });

  const generatedInvoices = (generatedData?.data || []) as GeneratedInvoice[];

  const accounts = (accountsData?.data || []) as Account[];
  const profiles = (profilesData?.data || []) as BusinessProfile[];
  const contracts = (contractsData?.data || []) as Contract[];

  // Populate form when editing
  useEffect(() => {
    if (scheduleData?.data) {
      const s = scheduleData.data as ScheduleData;
      setAccountId(s.accountId);
      setBusinessProfileId(s.businessProfileId);
      setContractId(s.contractId || '');
      setName(s.name);
      setFrequency(s.frequency);
      setBillingDayOfMonth(s.billingDayOfMonth);
      setStartDate(s.startDate?.split('T')[0] || '');
      setEndDate(s.endDate?.split('T')[0] || '');
      setStatus(s.status);
      setPaymentTerms(s.paymentTerms);
      setTaxRatePercent(Number(((s.taxRate || 0) * 100).toFixed(4)));
      setGracePeriodDays(s.gracePeriodDays);
      setSendInvoiceDaysBefore(s.sendInvoiceDaysBefore);
      setEnablePaymentReminders(s.enablePaymentReminders);
      setAutoGenerate(s.autoGenerate ?? true);
      setNextBillingDate(s.nextBillingDate || null);
      setReminderDays(parseReminderDays(s.reminderDaysBeforeDue));
      setNotes(s.notes || '');
      if (s.items && s.items.length > 0) {
        setItems(s.items.map((item: LineItem, idx: number) => ({
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          sortOrder: idx,
          itemType: item.itemType || 'service',
        })));
      }
    }
  }, [scheduleData]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createRecurringSchedule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringSchedules'] });
      navigate('/billing/recurring');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.updateRecurringSchedule(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recurringSchedules'] });
      queryClient.invalidateQueries({ queryKey: ['recurringSchedule', id] });
      navigate('/billing/recurring');
    },
  });

  function addItem() {
    setItems([...items, { description: '', quantity: 1, unitPrice: 0, sortOrder: items.length, itemType: 'service' }]);
  }

  function removeItem(index: number) {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  }

  function updateItem(index: number, field: keyof LineItem, value: string | number) {
    setItems(items.map((item, i) => i === index ? { ...item, [field]: value } : item));
  }

  const taxRate = taxRatePercent / 100;
  const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  const taxAmount = subtotal * taxRate;
  const total = subtotal + taxAmount;

  // Preview of when the next invoice lands. Existing schedules keep their stored
  // date unless the billing day changed, which realigns it server-side.
  const upcomingDate = useMemo(() => {
    if (isEdit && nextBillingDate) {
      const stored = new Date(nextBillingDate);
      if (!isNaN(stored.getTime()) && stored.getUTCDate() === Math.min(billingDayOfMonth, 31)) {
        return stored;
      }
    }
    return firstBillingDate(startDate, billingDayOfMonth);
  }, [isEdit, nextBillingDate, startDate, billingDayOfMonth]);

  function toggleReminderDay(day: number) {
    setReminderDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day].sort((a, b) => b - a)
    );
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const reminderDaysArray = `[${[...reminderDays].sort((a, b) => b - a).join(',')}]`;

    const payload: Record<string, unknown> = {
      accountId,
      businessProfileId,
      contractId: contractId || null,
      applicationId: null,
      name,
      frequency,
      billingDayOfMonth,
      startDate,
      endDate: endDate || null,
      status,
      paymentTerms,
      taxRate,
      gracePeriodDays,
      sendInvoiceDaysBefore,
      enablePaymentReminders,
      reminderDaysBeforeDue: reminderDaysArray,
      autoGenerate,
      notes: notes || null,
      items: items.map((item, idx) => ({
        description: item.description,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        sortOrder: idx,
        itemType: item.itemType,
      })),
    };

    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/billing/recurring')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isEdit ? 'Edit Schedule' : 'New Recurring Schedule'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader><CardTitle>Schedule Details</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Schedule Name *</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="e.g. Monthly Hosting" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Account *</Label>
                <select
                  value={accountId}
                  onChange={(e) => { setAccountId(e.target.value); setContractId(''); }}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select account</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Business Profile *</Label>
                <select
                  value={businessProfileId}
                  onChange={(e) => setBusinessProfileId(e.target.value)}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select profile</option>
                  {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Contract (optional)</Label>
                <select
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">No contract</option>
                  {contracts.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="paused">Paused</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Billing Schedule */}
        <Card>
          <CardHeader><CardTitle>Billing Schedule</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Frequency *</Label>
                <Select value={frequency} onValueChange={setFrequency}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="semi_annual">Semi-Annual</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bills on *</Label>
                <Select
                  value={String(billingDayOfMonth)}
                  onValueChange={(v) => setBillingDayOfMonth(Number(v))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                      <SelectItem key={day} value={String(day)}>
                        {billingDayLabel(day)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Payment Terms *</Label>
                <Select value={paymentTerms} onValueChange={setPaymentTerms}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                    <SelectItem value="Net 15">Net 15</SelectItem>
                    <SelectItem value="Net 30">Net 30</SelectItem>
                    <SelectItem value="Net 45">Net 45</SelectItem>
                    <SelectItem value="Net 60">Net 60</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div>
                <Label>Tax Rate (%)</Label>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={taxRatePercent}
                    onChange={(e) => setTaxRatePercent(parseFloat(e.target.value) || 0)}
                    placeholder="e.g. 8.25"
                    className="pr-7"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
                </div>
              </div>
            </div>

            {/* Cadence preview */}
            {upcomingDate && (
              <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <CalendarClock className="h-4 w-4 text-muted-foreground shrink-0" />
                <span>
                  Next invoice on <strong>{formatDate(upcomingDate.toISOString())}</strong>, then{' '}
                  {frequency === 'monthly' ? 'every month' :
                   frequency === 'quarterly' ? 'every 3 months' :
                   frequency === 'semi_annual' ? 'every 6 months' : 'every year'}
                  {endDate ? ` until ${formatDate(endDate)}` : ' with no end date'}.
                </span>
              </div>
            )}

            <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/30">
              <input
                type="checkbox"
                checked={autoGenerate}
                onChange={(e) => setAutoGenerate(e.target.checked)}
                className="h-4 w-4 mt-0.5"
              />
              <span className="text-sm">
                <span className="font-medium flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5" />
                  Create invoices automatically
                </span>
                <span className="block text-muted-foreground text-xs mt-0.5">
                  Draft invoices are created on each billing date without anyone clicking Generate.
                  Turn this off to review each one manually from the Generate Invoices tab.
                </span>
              </span>
            </label>
          </CardContent>
        </Card>

        {/* Email & Reminders */}
        <Card>
          <CardHeader><CardTitle>Email & Reminders</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Send Invoice (days before billing)</Label>
                <Input
                  type="number"
                  min={0}
                  value={sendInvoiceDaysBefore}
                  onChange={(e) => setSendInvoiceDaysBefore(parseInt(e.target.value) || 0)}
                />
                <p className="text-xs text-muted-foreground mt-1">0 = send on billing date</p>
              </div>
              <div>
                <Label>Grace Period (days)</Label>
                <Input
                  type="number"
                  min={0}
                  value={gracePeriodDays}
                  onChange={(e) => setGracePeriodDays(parseInt(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="enableReminders"
                checked={enablePaymentReminders}
                onChange={(e) => setEnablePaymentReminders(e.target.checked)}
                className="h-4 w-4"
              />
              <Label htmlFor="enableReminders">Enable payment reminders</Label>
            </div>
            {enablePaymentReminders && (
              <div>
                <Label>Send reminders before the due date</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {REMINDER_PRESETS.map((day) => {
                    const active = reminderDays.includes(day);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleReminderDay(day)}
                        className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                          active
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'hover:bg-muted'
                        }`}
                      >
                        {day} day{day === 1 ? '' : 's'} before
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {reminderDays.length === 0
                    ? 'No reminders selected — the customer will only get the invoice itself.'
                    : `Reminders at ${[...reminderDays].sort((a, b) => b - a).join(', ')} day(s) before due.`}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Line Items</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="h-4 w-4 mr-1" /> Add Item
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <table className="w-full">
              <thead>
                <tr className="border-b text-sm text-muted-foreground">
                  <th className="text-left pb-2 w-[40%]">Description</th>
                  <th className="text-left pb-2 w-[15%]">Type</th>
                  <th className="text-right pb-2 w-[12%]">Qty</th>
                  <th className="text-right pb-2 w-[15%]">Unit Price</th>
                  <th className="text-right pb-2 w-[13%]">Amount</th>
                  <th className="w-[5%]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="py-2 pr-2">
                      <Input
                        value={item.description}
                        onChange={(e) => updateItem(idx, 'description', e.target.value)}
                        placeholder="Description"
                        required
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <select
                        value={item.itemType}
                        onChange={(e) => updateItem(idx, 'itemType', e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                      >
                        <option value="subscription">Subscription</option>
                        <option value="consulting">Consulting</option>
                        <option value="service">Service</option>
                        <option value="product">Product</option>
                        <option value="other">Other</option>
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={item.unitPrice}
                        onChange={(e) => updateItem(idx, 'unitPrice', parseFloat(e.target.value) || 0)}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 pr-2 text-right text-sm font-medium">
                      ${(item.quantity * item.unitPrice).toFixed(2)}
                    </td>
                    <td className="py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(idx)}
                        disabled={items.length <= 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="mt-4 flex justify-end">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tax ({taxRatePercent.toFixed(2)}%)</span>
                  <span>${taxAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-base border-t pt-1">
                  <span>Total per invoice</span>
                  <span>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardContent className="pt-6">
            <Label>Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Internal notes..." />
          </CardContent>
        </Card>

        {/* Invoices produced by this schedule */}
        {isEdit && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Generated Invoices
                </CardTitle>
                {generatedInvoices.length > 0 && (
                  <span className="text-sm text-muted-foreground">
                    {generatedInvoices.length} invoice{generatedInvoices.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {generatedInvoices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No invoices yet. The first one is created on the next billing date
                  {autoGenerate ? ' automatically' : ' once you generate it from the Generate Invoices tab'}.
                </p>
              ) : (
                <div className="divide-y">
                  {generatedInvoices.map((inv) => (
                    <button
                      key={inv.id}
                      type="button"
                      className="flex w-full items-center justify-between py-2 text-left hover:bg-muted/40 rounded px-2 -mx-2"
                      onClick={() => navigate(`/billing/invoices/${inv.id}`)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-muted-foreground">#{inv.invoiceNumber}</span>
                        <span className="text-sm">{formatDate(inv.issueDate)}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium">{formatCurrency(inv.total)}</span>
                        <Badge variant={invoiceStatusVariant[inv.status] || 'secondary'} className="w-20 justify-center">
                          {inv.status}
                        </Badge>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate('/billing/recurring')}>Cancel</Button>
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Save className="h-4 w-4 mr-2" />
            {isEdit ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </div>
      </form>
    </div>
  );
}
